import { useCallback, useEffect, useState } from 'react';

/**
 * Os quatro estados de uma tela de análises, num tipo só.
 *
 * O CLAUDE.md exige que toda tela tenha carregando, vazio, erro e sucesso. Um
 * tipo discriminado obriga o componente a tratar os quatro — esquecer um vira
 * erro de tipo, não tela quebrada em produção. Mesmo desenho de
 * `parametrizacao/useDominio.ts`, generalizado porque aqui cada tela carrega uma
 * forma diferente.
 */
export type EstadoDaColecao<T> =
  | { situacao: 'carregando' }
  | { situacao: 'erro'; mensagem: string }
  | { situacao: 'vazio' }
  | { situacao: 'pronto'; dado: T };

export interface RetornoDaColecao<T> {
  estado: EstadoDaColecao<T>;
  recarregar: () => void;
}

/**
 * @param carregarDado  função que busca o dado. Precisa ser estável entre
 *                      renderizações (declarada fora do componente, ou em
 *                      `useCallback`), senão o efeito roda a cada render.
 * @param estaVazio     diz se a resposta, embora recebida, não tem nada para
 *                      mostrar. Sem isso não há como distinguir vazio de pronto.
 */
export function useColecao<T>(
  carregarDado: () => Promise<T>,
  estaVazio: (dado: T) => boolean,
): RetornoDaColecao<T> {
  const [estado, setEstado] = useState<EstadoDaColecao<T>>({ situacao: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  const recarregar = useCallback(() => {
    setTentativa((anterior) => anterior + 1);
  }, []);

  useEffect(() => {
    let vivo = true;
    setEstado({ situacao: 'carregando' });

    carregarDado()
      .then((dado) => {
        if (!vivo) {
          return;
        }
        setEstado(estaVazio(dado) ? { situacao: 'vazio' } : { situacao: 'pronto', dado });
      })
      .catch((erro: unknown) => {
        if (!vivo) {
          return;
        }
        setEstado({
          situacao: 'erro',
          mensagem: erro instanceof Error ? erro.message : 'Erro desconhecido.',
        });
      });

    return () => {
      // Tela trocada antes da resposta chegar: sem isto, o setState cai em
      // componente desmontado e o React avisa no console.
      vivo = false;
    };
    // `estaVazio` é predicado puro declarado no módulo da tela; incluí-lo aqui
    // só recriaria o efeito sem mudar o resultado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarDado, tentativa]);

  return { estado, recarregar };
}
